import { useEffect, useMemo, useRef, useState } from 'react';
import { getLogger } from '@packages/core/logging';
import { docsService } from '@web/services/singletons';
import {
    browseDocumentCodes,
    countDocumentCodes,
    prepareDocumentCodes,
    rankDocumentCodes,
    type DocumentCodeFilter,
    type PreparedDocumentCode,
} from '../document-code.utils';

const logger = getLogger('vin-sessions');

export type DocumentCodeScope = {
    publicationTypeIds: string[];
    carlineIds: string[];
    modelYear?: number;
};

export type DocumentCodeLookup = {
    options: PreparedDocumentCode[];
    isLoading: boolean;
    search: (query: string, scope: DocumentCodeScope, limit?: number) => PreparedDocumentCode[];
    browse: (scope: DocumentCodeScope, limit: number) => PreparedDocumentCode[];
    total: (scope: DocumentCodeScope, query?: string) => number;
};

export const useDocumentCodes = (): DocumentCodeLookup => {
    const [options, setOptions] = useState<PreparedDocumentCode[]>([]);
    const [typeIds, setTypeIds] = useState<string[]>([]);
    const [carlineIds, setCarlineIds] = useState<string[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const hasLoaded = useRef(false);

    useEffect(() => {
        if (hasLoaded.current) {
            return;
        }
        const controller = new AbortController();
        setIsLoading(true);
        docsService
            .getDocumentCodes({ signal: controller.signal })
            .then((response) => {
                if (controller.signal.aborted) {
                    return;
                }
                if (response.success) {
                    hasLoaded.current = true;
                    setTypeIds(response.data.types ?? []);
                    setCarlineIds(response.data.carlines ?? []);
                    setOptions(prepareDocumentCodes(response.data.codes ?? []));
                }
            })
            .catch((error) => logger.error('Failed to fetch the document codes', { error }))
            .finally(() => {
                if (!controller.signal.aborted) {
                    setIsLoading(false);
                }
            });
        return () => controller.abort();
    }, []);

    return useMemo(() => {
        const indexOfType = new Map(typeIds.map((id, index) => [id, index]));
        const indexOfCarline = new Map(carlineIds.map((id, index) => [id, index]));

        const toFilter = (scope: DocumentCodeScope): DocumentCodeFilter => ({
            types: scope.publicationTypeIds
                .map((id) => indexOfType.get(id))
                .filter((index): index is number => index !== undefined),
            carlines: scope.carlineIds
                .map((id) => indexOfCarline.get(id))
                .filter((index): index is number => index !== undefined),
            modelYear: scope.modelYear,
        });

        return {
            options,
            isLoading,
            search: (query: string, scope: DocumentCodeScope, limit?: number) =>
                rankDocumentCodes(options, query, toFilter(scope), limit),
            browse: (scope: DocumentCodeScope, limit: number) => browseDocumentCodes(options, toFilter(scope), limit),
            total: (scope: DocumentCodeScope, query?: string) => countDocumentCodes(options, toFilter(scope), query),
        };
    }, [options, typeIds, carlineIds, isLoading]);
};
